'use client';

import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, TextButton, TextInput } from '@galapagos/design-system/web';
import {
  capIdentityText,
  GENDER_IDENTITY_OPTIONS,
  PRONOUN_OPTIONS,
  SELF_DESCRIBE,
  type GenderInfoSelection,
} from '../profile/gender-info';

interface GenderInfoFieldsProps {
  open: boolean;
  onToggle: () => void;
  identity: GenderInfoSelection;
  onIdentityChange: (selection: GenderInfoSelection) => void;
  pronouns: GenderInfoSelection;
  onPronounsChange: (selection: GenderInfoSelection) => void;
}

/**
 * The birth-date step's optional "Add gender info" section (production 1.7a):
 * a link that shows and hides a gender identity and a pronouns dropdown, each
 * with a Self Describe choice that opens a free-text field under it. Render it
 * inside the step's private fields, so neither the picks nor the typed words
 * reach autocapture or session replay.
 */
export function GenderInfoFields({
  open,
  onToggle,
  identity,
  onIdentityChange,
  pronouns,
  onPronounsChange,
}: GenderInfoFieldsProps) {
  const { t } = useTranslation('signup');
  const sectionId = useId();

  return (
    <>
      <div>
        <TextButton
          type="Link"
          label={t(open ? 'birthDate.genderInfoHide' : 'birthDate.genderInfoShow')}
          aria-expanded={open}
          aria-controls={sectionId}
          actionId="signup.birth-date-screen-gender-info.toggle"
          onPress={onToggle}
          data-testid="signup-gender-info-toggle"
        />
      </div>
      {/* Always mounted so the link's aria-controls names a real element;
          the group class's flex display would otherwise win over `hidden`. */}
      <div
        id={sectionId}
        className="signup-step__group"
        hidden={!open}
        style={open ? undefined : { display: 'none' }}
      >
        {open ? (
          <>
            <Select
              actionId="signup.birth-date-screen-gender-identity.change"
              toggleActionId="signup.birth-date-screen-gender-identity.toggle"
              label={t('birthDate.genderIdentityLabel')}
              options={GENDER_IDENTITY_OPTIONS.map((option) => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
              value={identity.choice}
              onChange={(choice) => onIdentityChange({ choice, text: identity.text })}
              data-testid="signup-gender-identity"
            />
            {identity.choice === SELF_DESCRIBE ? (
              <TextInput
                label={t('birthDate.selfDescribeLabel')}
                aria-label={t('birthDate.genderIdentitySelfDescribeA11y')}
                actionId="signup.birth-date-screen-gender-identity-self-describe.change"
                value={identity.text}
                autoComplete="off"
                onChangeText={(text) =>
                  onIdentityChange({ choice: SELF_DESCRIBE, text: capIdentityText(text) })
                }
                data-testid="signup-gender-identity-self-describe"
              />
            ) : null}
            <Select
              actionId="signup.birth-date-screen-pronouns.change"
              toggleActionId="signup.birth-date-screen-pronouns.toggle"
              label={t('birthDate.pronounsLabel')}
              options={PRONOUN_OPTIONS.map((option) => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
              value={pronouns.choice}
              onChange={(choice) => onPronounsChange({ choice, text: pronouns.text })}
              data-testid="signup-pronouns"
            />
            {pronouns.choice === SELF_DESCRIBE ? (
              <TextInput
                label={t('birthDate.selfDescribeLabel')}
                aria-label={t('birthDate.pronounsSelfDescribeA11y')}
                actionId="signup.birth-date-screen-pronouns-self-describe.change"
                value={pronouns.text}
                autoComplete="off"
                onChangeText={(text) =>
                  onPronounsChange({ choice: SELF_DESCRIBE, text: capIdentityText(text) })
                }
                data-testid="signup-pronouns-self-describe"
              />
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}
